
'use server';

/**
 * @fileOverview تدفق تحليل أصوات السيارات بالذكاء الاصطناعي.
 * 
 * - analyzeCarSound - يحلل التسجيلات الصوتية لاكتشاف الأعطال الميكانيكية.
 * - AnalyzeSoundInput - مدخلات الصوت كـ Data URI.
 * - AnalyzeSoundOutput - التحليل الصوتي والتشخيص المقترح.
 */

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const AnalyzeSoundInputSchema = z.object({
  audioDataUri: z.string().describe("تسجيل صوتي للعطل بصيغة Data URI."),
});

export type AnalyzeSoundInput = z.infer<typeof AnalyzeSoundInputSchema>;

const AnalyzeSoundOutputSchema = z.object({
  soundDescription: z.string().describe("وصف الذكاء الاصطناعي للصوت المكتشف."),
  possibleFaults: z.array(z.string()).describe("الأعطال المحتملة بناءً على الصوت."),
  recommendation: z.string().describe("النصيحة الفورية للمستخدم."),
});

export type AnalyzeSoundOutput = z.infer<typeof AnalyzeSoundOutputSchema>;

const audioPrompt = ai.definePrompt({
  name: 'carAudioAnalysis',
  input: { schema: AnalyzeSoundInputSchema },
  output: { schema: AnalyzeSoundOutputSchema },
  prompt: `أنت خبير ميكانيكا محترف. استمع إلى هذا التسجيل الصوتي للسيارة وحلل الأصوات المسموعة (مثل طقطقة، صرير، ونين، أو اهتزاز).
  
  المطلوب:
  1. وصف دقيق للصوت باللغة العربية.
  2. قائمة بالأعطال الميكانيكية التي قد تسبب هذا الصوت.
  3. توصية عاجلة (مثلاً: توقف فوراً، افحص القشاط، أو توجه للورشة).

  الصوت: {{media url=audioDataUri}}`,
});

export async function analyzeCarSound(input: AnalyzeSoundInput): Promise<AnalyzeSoundOutput> {
  return analyzeCarSoundFlow(input);
}

const analyzeCarSoundFlow = ai.defineFlow(
  {
    name: 'analyzeCarSoundFlow',
    inputSchema: AnalyzeSoundInputSchema,
    outputSchema: AnalyzeSoundOutputSchema,
  },
  async (input) => {
    const { output } = await audioPrompt(input);
    return output!;
  }
);
